import React from 'react';
import { CheckIcon, GraduationCapIcon, MonitorIcon, ShieldCheckIcon, TrophyIcon } from 'lucide-react';
import { MatchedSchool } from '../services/matchingApi';
import { normalizeSchool } from '../utils/schoolFields';
import { extractListItems } from '../utils/sanitizeHtml';

/** Partners send 1–8 bullets; cards must stay comparable in height. */
const MAX_BULLETS = 3;
interface MatchedSchoolCardProps {
  school: MatchedSchool;
  index: number;
}

/**
 * One API-sourced school in the results list.
 *
 * Mobile and desktop are laid out separately because they answer the question
 * differently. On desktop there is room for a rank column, the partner headline,
 * and a dedicated action column. On mobile the logo IS the identity — it is
 * given roughly a third of the card beside the selling points, the headline is
 * dropped, and the action spans the full width. The hidden layout is
 * display:none, so it is out of the tab order and the accessibility tree.
 */
export function MatchedSchoolCard({
  school,
  index
}: MatchedSchoolCardProps) {
  const normalized = normalizeSchool(school as Record<string, unknown>);
  const {
    name,
    logo,
    accreditation,
    headline,
    clickUrl,
    highlight
  } = normalized;
  const bullets = extractListItems(normalized.bodyHtml).slice(0, MAX_BULLETS);
  const position = ((school as Record<string, unknown>).mPosition || index + 1) as number;
  const isFirst = index === 0;
  const initials = (name || 'SC').split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 3).toUpperCase();
  const rankCircle = <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isFirst ? 'bg-navy text-white' : 'border-2 border-gray-200 text-gray-500'}`} data-id="element-709" data-mp="1saenfo4eq22z">
      <span className="text-sm font-bold" data-id="element-710" data-mp="10ji8mhysdnd2">{position}</span>
      <span className="sr-only" data-id="element-711" data-mp="10gw1ukjpzbvw">Ranked number {position}</span>
    </div>;
  const highlightPill = highlight ? <span className="inline-flex items-center text-center bg-light border border-gray-200 text-navy text-[11px] sm:text-xs font-semibold leading-tight px-2.5 py-1 rounded-full" data-id="element-712" data-mp="27vho7cddirhn">
      {highlight}
    </span> : null;
  const logoBlock =
  // h-full matters: without it the image's max-h-full has no height to
  // resolve against and a tall wordmark overflows onto the school name.
  <div className="w-full h-full flex items-center justify-center" data-id="element-713" data-mp="2cokar0jqh0oc">
      {logo ? <img src={logo} alt={`${name} logo`} className="max-w-full max-h-full object-contain" loading="lazy" data-id="element-714" data-mp="1oypzgnf2bk67" /> : <div className="flex flex-col items-center gap-1 text-navy" data-id="element-715" data-mp="1xy38jez9y6qe">
          <GraduationCapIcon className="w-7 h-7" aria-hidden="true" data-id="element-716" data-mp="1tv6xc9c2dcwh" />
          <span className="text-sm font-bold" data-id="element-717" data-mp="1ty2sxr0fvphr">{initials}</span>
        </div>}
    </div>;
  const bulletList = bullets.length > 0 ? <ul className="space-y-2" data-id="element-718" data-mp="359tz43el891u">
        {bullets.map(bullet => <li key={bullet} className="flex items-start gap-2" data-id="element-719" data-mp="3cuo4lqp23yqv">
            <CheckIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-green-600" aria-hidden="true" data-id="element-720" data-mp="3cwh9p1k0uawr" />
            <span className="text-[13px] sm:text-sm text-gray-700 leading-relaxed" data-id="element-721" data-mp="32wqcut7znjti">
              {bullet}
            </span>
          </li>)}
      </ul> : null;
  const accreditationLine = accreditation ? <p className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs text-gray-500" data-id="element-722" data-mp="3vlxbds7g09er">
      <ShieldCheckIcon className="w-3.5 h-3.5 flex-shrink-0 text-green-600" aria-hidden="true" data-id="element-723" data-mp="4eumj7f2uf7p" />
      {accreditation}
    </p> : null;
  const ctaButton = clickUrl ? <a href={clickUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center text-center w-full min-h-[56px] lg:min-h-[60px] px-5 py-4 text-base font-semibold rounded-full text-white bg-primary shadow-ds-md hover:bg-primary-hover hover:shadow-ds-lg lg:hover:-translate-y-0.5 active:bg-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary transition-[background-color,box-shadow,transform] duration-150 ease-out" style={{
    backgroundColor: "#5cb335",
    backgroundImage: "none"
  }} data-id="element-724" data-mp="3qtnsux8nqbmi">
      View Programs
    </a> : null;
  const onlineNote = <p className="flex items-center justify-center gap-1.5 text-xs font-semibold text-gray-600" data-id="element-725" data-mp="2gqpaxq1eibqa">
      <MonitorIcon className="w-3.5 h-3.5 flex-shrink-0 text-green-600" aria-hidden="true" data-id="element-726" data-mp="3ebv1qnpb13h8" />
      100% Online
    </p>;
  return <article className="relative" data-id="element-727" data-mp="34mbllpsz7nq2">
      {isFirst && <div className="rounded-t-xl px-4 py-2.5 sm:py-3 flex items-center justify-center gap-2 bg-navy" data-id="element-728" data-mp="34kqr206nw4s7">
          <TrophyIcon className="w-4 h-4 flex-shrink-0 text-gold-light" aria-hidden="true" data-id="element-729" data-mp="2zlw54pdee03v" />
          <span className="text-white text-xs sm:text-sm font-bold tracking-wide" data-id="element-730" data-mp="3sbw1fztu24v4">
            #1 Recommended School
          </span>
        </div>}

      <div className={`bg-white transition-shadow duration-200 ease-out ${isFirst ? 'rounded-b-xl border-2 border-t-0 border-navy/20 shadow-lg hover:shadow-xl' : 'rounded-xl border border-gray-200 shadow-sm hover:shadow-md'}`} data-id="element-731" data-mp="4hqskqxslqdk">
        {/* ---------- Mobile ---------- */}
        <div className="lg:hidden p-4" data-id="element-732" data-mp="nerxycv9dsi1">
          {/* Rank reads as "#1" rather than a bare circle — at this size a
              number on its own is ambiguous. */}
          <div className="flex items-center justify-between gap-3 pb-3" data-id="element-733" data-mp="re0t9m5y5yfp">
            <span className={`inline-block flex-shrink-0 rounded-full border px-2.5 py-1 text-xs font-bold leading-none ${isFirst ? 'bg-[#14479E] border-[#14479E] text-white' : 'bg-[#F4F7FC] border-gray-200 text-[#14479E]'}`} data-id="element-734" data-mp="w84zg37e22fq">
              {`#${position}`}
              <span className="sr-only" data-id="element-735" data-mp="lfpalh5drnd0"> ranked school</span>
            </span>
            {highlightPill}
          </div>

          {/* The logo carries identity on mobile, so it leads at full width —
              which also lets the bullets run the width of the card. */}
          <div className="h-20 flex items-center justify-center overflow-hidden" data-id="element-736" data-mp="v7uyofsihkmx">
            {logoBlock}
          </div>

          <div className="mt-4 text-center" data-id="element-737" data-mp="1466o4ehn6cds">
            <h3 className="text-lg font-bold text-navy leading-tight" data-id="element-738" data-mp="1455gkjzu480t">
              {name}
            </h3>
            {accreditationLine && <div className="mt-1 flex justify-center" data-id="element-739" data-mp="1e4megf8uhhm8">
                {accreditationLine}
              </div>}
          </div>

          {bulletList && <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-740" data-mp="196ba2k1bjzbe">
              {bulletList}
            </div>}

          <div className="mt-4 space-y-2" data-id="element-741" data-mp="3awwepkyqvlau">
            {ctaButton}
            {onlineNote}
          </div>
        </div>

        {/* ---------- Desktop ---------- */}
        <div className="hidden lg:flex p-7 gap-8" data-id="element-742" data-mp="1qfnjgxfd6c8a">
          <div className="flex-1 min-w-0 flex gap-4" data-id="element-743" data-mp="1vegkvrhdmcs3">
            {rankCircle}

            <div className="flex-1 min-w-0" data-id="element-744" data-mp="1gp2b4i9w1tc6">
              <h3 className="text-2xl font-bold text-navy leading-tight" data-id="element-745" data-mp="1go9ck4l32a54">
                {name}
              </h3>
              {accreditationLine && <div className="mt-2" data-id="element-746" data-mp="17omarhofz2bj">{accreditationLine}</div>}
              {headline && <p className="mt-4 text-lg font-bold text-crimson leading-snug" data-id="element-747" data-mp="1bqhaihqq57n7">
                  {headline}
                </p>}
              {bulletList && <div className="mt-4" data-id="element-748" data-mp="xsawwn97e4ez">{bulletList}</div>}
            </div>
          </div>

          <div className="w-60 flex-shrink-0 border-l border-gray-100 pl-8 flex flex-col items-center justify-center gap-4" data-id="element-749" data-mp="12pjgmsa02gic">
            {highlightPill}
            <div className="w-full h-20 flex items-center justify-center" data-id="element-750" data-mp="2x3u3wway52m0">
              {logoBlock}
            </div>
            {ctaButton}
            {onlineNote}
          </div>
        </div>
      </div>
    </article>;
}
export function DidYouKnowBanner() {
  return <p className="flex flex-wrap items-center justify-center gap-x-2 py-3 text-sm text-gray-600" data-id="element-751" data-mp="1b45r1sbwgywk">
      <span className="font-bold text-gray-800" data-id="element-752" data-mp="1652l85o3ionj">DID YOU KNOW?</span>
      <span data-id="element-753" data-mp="164stacjhq7f7">Online enrollments continue to grow year over year.</span>
    </p>;
}